// src/components/analytics/KPIFullReportModal.js
// --- UPDATE (ML Ops) ----------------------------------------------------------------
// Everything in this report is now real. Before: the history was generated with Math.random()
// (a different chart on every render), the "AI executive summary", prescriptive actions and
// correlation percentages were hard-coded text, and Print / Export PDF / Email did nothing.
// Now:
//   • History: GET /ml-ops/insights/kpi-history?days=N — daily series computed in SQL from
//     work orders, SLA deadlines, costs and sensor alerts (System Uptime has no history source
//     in the database, so the report says so instead of inventing one).
//   • Anomalies: statistical (|z| ≥ 2.5 against a trailing 7-day window).
//   • Analysis: trend from a least-squares fit, target adherence, best / worst day, anomalies —
//     every sentence is computed from the series and says how many days it is based on.
//   • Recommended actions: rules on the computed state, each linking to the screen to act on.
//   • Correlations: Pearson r against the other KPIs (same day and next day), with n.
//   • Export CSV, Export PDF (jsPDF + autotable, loaded on demand), Print, Email (mail client
//     with the summary; attach the CSV/PDF).
// Props are unchanged: { isOpen, onClose, kpiKey, kpis }.
// --- END UPDATE ---------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Box, Flex, VStack, HStack, Heading, Text, Button, Badge, SimpleGrid, useColorModeValue,
  Tabs, TabList, TabPanels, Tab, TabPanel, Icon, Select, Progress, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, AlertDescription, Skeleton, Link, Menu, MenuButton, MenuList, MenuItem, useToast,
} from '@chakra-ui/react';
import {
  MdAnalytics, MdTrendingUp, MdTrendingDown, MdTrendingFlat, MdFileDownload, MdPrint, MdEmail,
  MdLightbulb, MdTimeline, MdWarning, MdPsychology, MdCompareArrows, MdExpandMore,
} from 'react-icons/md';
import {
  ComposedChart, Area, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Brush,
} from 'recharts';
import mlOpsService from '../../services/mlOpsService';

// Map raw keys to human-readable names and preferred colors
const KPI_CONFIG = {
  systemUptime: { name: 'System Uptime', color: 'green', unit: '%', target: 99.9, higherIsBetter: true },
  openWorkOrders: { name: 'Open Work Orders', color: 'blue', unit: '', target: 50, higherIsBetter: false },
  slaCompliance: { name: 'SLA Compliance', color: 'purple', unit: '%', target: 95, higherIsBetter: true },
  totalMaintenanceCost: { name: 'Maintenance Cost', color: 'orange', unit: '$', target: 100000, higherIsBetter: false, perDayTarget: (t, days) => t / Math.max(days, 1) },
  criticalAlerts: { name: 'Critical Alerts', color: 'red', unit: '', target: 0, higherIsBetter: false },
};
const DAYS = { '7D': 7, '30D': 30, '90D': 90 };

const fmtVal = (v, unit) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (unit === '$') return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit}`;
};
const shortDate = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: 'short', day: '2-digit', timeZone: 'UTC' });
};

// ── statistics ───────────────────────────────────────────────────────────────
const mean = (a) => a.reduce((s, x) => s + x, 0) / (a.length || 1);
const std = (a) => { const m = mean(a); return Math.sqrt(mean(a.map(x => (x - m) ** 2))); };
function linearTrend(points) {
  const pts = points.filter(p => p.value !== null && p.value !== undefined);
  if (pts.length < 3) return null;
  const xs = pts.map((_, i) => i); const ys = pts.map(p => Number(p.value));
  const mx = mean(xs); const my = mean(ys);
  const num = xs.reduce((s, x, i) => s + (x - mx) * (ys[i] - my), 0);
  const den = xs.reduce((s, x) => s + (x - mx) ** 2, 0) || 1;
  const slope = num / den;
  const changePct = my !== 0 ? ((slope * (pts.length - 1)) / Math.abs(my)) * 100 : 0;
  return { slope, changePct, n: pts.length };
}
function detectAnomalies(series) {
  const out = [];
  series.forEach((p, i) => {
    if (p.value === null || p.value === undefined) return;
    const window = series.slice(Math.max(0, i - 7), i).map(x => x.value).filter(v => v !== null && v !== undefined);
    if (window.length < 5) return;
    const m = mean(window); const s = std(window);
    if (s === 0) { if (p.value !== m && Math.abs(p.value - m) > Math.max(1, Math.abs(m) * 0.5)) out.push({ ...p, expected: m, z: null }); return; }
    const z = (p.value - m) / s;
    if (Math.abs(z) >= 2.5) out.push({ ...p, expected: m, z });
  });
  return out;
}
function pearson(a, b) {
  const pairs = a.map((x, i) => [x, b[i]]).filter(([x, y]) => x !== null && x !== undefined && y !== null && y !== undefined);
  if (pairs.length < 5) return null;
  const xs = pairs.map(p => p[0]); const ys = pairs.map(p => p[1]);
  const sx = std(xs); const sy = std(ys);
  if (!sx || !sy) return null;
  const mx = mean(xs); const my = mean(ys);
  return { r: mean(pairs.map(([x, y]) => (x - mx) * (y - my))) / (sx * sy), n: pairs.length };
}
const strength = (r) => { const a = Math.abs(r); return a >= 0.7 ? ['Strong', 'red'] : a >= 0.4 ? ['Moderate', 'orange'] : a >= 0.2 ? ['Weak', 'yellow'] : ['None', 'gray']; };

function recommendations(kpiKey, s, cfg) {
  const recs = [];
  const worse = s.trend && (cfg.higherIsBetter ? s.trend.changePct < -5 : s.trend.changePct > 5);
  if (kpiKey === 'openWorkOrders') {
    if (worse) recs.push({ when: 'Immediate', color: 'red', title: 'Backlog is growing', text: `Open work orders rose ${Math.abs(s.trend.changePct).toFixed(0)}% over the period. Review unassigned jobs and let the AI Dispatcher spread the load.`, link: '/admin/workorders', linkLabel: 'Open Work Orders' });
    if (s.current > cfg.target) recs.push({ when: 'This week', color: 'orange', title: 'Above the backlog target', text: `${fmtVal(s.current, '')} open vs a target of ${cfg.target}. Check SLA at-risk jobs first.`, link: '/admin/sla-ai', linkLabel: 'SLA Command Center' });
  }
  if (kpiKey === 'slaCompliance') {
    if (s.avg !== null && s.avg < cfg.target) recs.push({ when: 'Immediate', color: 'red', title: 'Below the SLA target', text: `Average ${s.avg.toFixed(1)}% vs ${cfg.target}%. The SLA Command Center shows which jobs and technicians are behind.`, link: '/admin/sla-ai', linkLabel: 'SLA Command Center' });
    if (worse) recs.push({ when: 'This week', color: 'orange', title: 'Compliance is declining', text: 'Consider retraining the AI Dispatcher so assignments use the latest reliability scores.', link: '/admin/ai/grading?tab=training', linkLabel: 'Training & Autopilot' });
  }
  if (kpiKey === 'totalMaintenanceCost') {
    if (s.anomalies.length) recs.push({ when: 'Immediate', color: 'red', title: 'Unusual spend days', text: `${s.anomalies.length} day(s) were far above the usual spend. Review the completed jobs on those days.`, link: '/admin/workorders', linkLabel: 'Work Orders' });
    if (worse) recs.push({ when: 'This month', color: 'orange', title: 'Costs are rising', text: 'Compare repair vs. replacement for the assets driving cost.', link: '/admin/analytics', linkLabel: 'Lifecycle cost & prescriptive actions' });
  }
  if (kpiKey === 'criticalAlerts') {
    if (s.total > 0) recs.push({ when: 'Immediate', color: 'red', title: 'Critical alerts occurred', text: `${s.total} critical alert(s) in the period. Check sensors with repeat alerts and the anomaly rules.`, link: '/admin/readings', linkLabel: 'Live Readings' });
    if (worse) recs.push({ when: 'This week', color: 'orange', title: 'Alerts are increasing', text: 'Review AI-suggested alert rules — rising alert volume can mean drift in sensor baselines.', link: '/admin/analytics', linkLabel: 'AI suggested rules' });
  }
  if (!recs.length) recs.push({ when: 'Ongoing', color: 'green', title: 'On track', text: 'No action needed from this KPI right now. Keep monitoring.', link: '/admin/dashboard', linkLabel: 'Dashboard' });
  return recs;
}

function downloadBlob(content, type, filename) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const KPIFullReportModal = ({ isOpen, onClose, kpiKey, kpis }) => {
  const toast = useToast();
  const [timeframe, setTimeframe] = useState('30D');
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  // Theme Hooks
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const bodyText = useColorModeValue('gray.700', 'gray.300');
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const brushFill = useColorModeValue('#F7FAFC', '#1A202C');
  const headerGradient = useColorModeValue('linear(to-r, blue.600, purple.600)', 'linear(to-r, blue.400, purple.400)');

  const config = useMemo(() => KPI_CONFIG[kpiKey] || { name: kpiKey, color: 'blue', unit: '', target: 0, higherIsBetter: true }, [kpiKey]);
  const days = DAYS[timeframe];
  const baseValue = kpis?.[kpiKey] ?? null;
  const reportedTrend = kpis?.[`${kpiKey}_trend`];
  const accent = useColorModeValue(`var(--chakra-colors-${config.color}-500)`, `var(--chakra-colors-${config.color}-300)`);
  const tint = useColorModeValue(`${config.color}.50`, `${config.color}.900`);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const h = await mlOpsService.getKpiHistory(days);
      if (mounted.current) setHistory(h);
    } catch (e) {
      if (mounted.current) setError(e);
    } finally { if (mounted.current) setLoading(false); }
  }, [days]);
  useEffect(() => { if (isOpen && kpiKey) load(); }, [isOpen, kpiKey, load]);

  const unavailable = Boolean(history && (history.unavailable || []).includes(kpiKey));
  const dailyTarget = config.perDayTarget ? config.perDayTarget(config.target, 30) : config.target;

  const model = useMemo(() => {
    const raw = history?.series?.[kpiKey] || [];
    const series = raw.map(p => ({ ...p, label: shortDate(p.date), target: dailyTarget }));
    const vals = series.map(p => p.value).filter(v => v !== null && v !== undefined);
    const anomalies = detectAnomalies(series);
    const anomalyDates = new Set(anomalies.map(a => a.date));
    const chart = series.map(p => ({ ...p, anomaly: anomalyDates.has(p.date) ? p.value : null }));
    const trend = linearTrend(series);
    const avg = vals.length ? mean(vals) : null;
    const current = baseValue !== null && baseValue !== undefined ? Number(baseValue) : (vals.length ? vals[vals.length - 1] : null);
    const best = vals.length ? series.filter(p => p.value !== null).reduce((a, b) => ((config.higherIsBetter ? b.value > a.value : b.value < a.value) ? b : a)) : null;
    const worst = vals.length ? series.filter(p => p.value !== null).reduce((a, b) => ((config.higherIsBetter ? b.value < a.value : b.value > a.value) ? b : a)) : null;
    const total = vals.reduce((s, v) => s + v, 0);
    const daysMeeting = vals.filter(v => (config.higherIsBetter ? v >= dailyTarget : v <= dailyTarget)).length;
    const correlations = Object.entries(history?.series || {}).filter(([k]) => k !== kpiKey).map(([k, other]) => {
      const a = series.map(p => p.value); const b = other.map(p => p.value);
      const same = pearson(a, b);
      const lag = pearson(a.slice(0, -1), b.slice(1)); // this KPI today vs the other tomorrow
      const pick = !same ? lag : !lag ? same : (Math.abs(lag.r) > Math.abs(same.r) + 0.1 ? { ...lag, lagged: true } : same);
      return { key: k, name: KPI_CONFIG[k]?.name || k, ...pick };
    }).filter(c => c.r !== undefined && c.r !== null).sort((x, y) => Math.abs(y.r) - Math.abs(x.r));
    return { series, chart, vals, anomalies, trend, avg, current, best, worst, total, daysMeeting, correlations };
  }, [history, kpiKey, baseValue, config.higherIsBetter, dailyTarget]);

  const s = model;
  const trendPct = s.trend ? s.trend.changePct : (typeof reportedTrend === 'number' ? reportedTrend : null);
  const good = trendPct === null ? null : (config.higherIsBetter ? trendPct >= 0 : trendPct <= 0);
  const TrendIcon = trendPct === null || Math.abs(trendPct) < 1 ? MdTrendingFlat : trendPct > 0 ? MdTrendingUp : MdTrendingDown;
  const recs = useMemo(() => recommendations(kpiKey, s, config), [kpiKey, s, config]);
  const adherence = s.vals.length ? Math.round((s.daysMeeting / s.vals.length) * 100) : null;

  const summary = useMemo(() => {
    if (!s.vals.length) return null;
    const parts = [];
    parts.push(`Over the last ${days} days (${s.vals.length} day${s.vals.length === 1 ? '' : 's'} with data), ${config.name} averaged ${fmtVal(s.avg, config.unit)}${s.current !== null ? ` and is now ${fmtVal(s.current, config.unit)}` : ''}.`);
    if (s.trend) parts.push(`The fitted trend is ${Math.abs(s.trend.changePct) < 1 ? 'flat' : `${s.trend.changePct > 0 ? 'up' : 'down'} ${Math.abs(s.trend.changePct).toFixed(1)}%`} across the period — ${good ? 'moving in the right direction' : 'moving the wrong way'}.`);
    parts.push(`The target (${fmtVal(dailyTarget, config.unit)}${config.perDayTarget ? ' per day' : ''}) was met on ${s.daysMeeting} of ${s.vals.length} days (${adherence}%).`);
    if (s.best && s.worst && s.best.date !== s.worst.date) parts.push(`Best day: ${shortDate(s.best.date)} (${fmtVal(s.best.value, config.unit)}); worst day: ${shortDate(s.worst.date)} (${fmtVal(s.worst.value, config.unit)}).`);
    parts.push(s.anomalies.length ? `${s.anomalies.length} day(s) were statistically unusual (more than 2.5 standard deviations from the previous week).` : 'No statistically unusual days were found.');
    if (s.correlations[0] && Math.abs(s.correlations[0].r) >= 0.4) parts.push(`It moves ${s.correlations[0].r > 0 ? 'with' : 'against'} ${s.correlations[0].name}${s.correlations[0].lagged ? ' (a day later)' : ''} (r = ${s.correlations[0].r.toFixed(2)}).`);
    return parts.join(' ');
  }, [s, days, config, good, dailyTarget, adherence]);

  // ── exports ──
  const rows = s.series.map(p => ({ date: p.date, value: p.value, target: dailyTarget, anomaly: s.anomalies.some(a => a.date === p.date) }));
  const fileBase = `flowsense-${kpiKey}-${days}d`;
  const exportCsv = () => {
    const csv = ['date,value,target,anomaly', ...rows.map(r => `${r.date},${r.value ?? ''},${r.target},${r.anomaly ? 'yes' : ''}`)].join('\n');
    downloadBlob(csv, 'text/csv;charset=utf-8', `${fileBase}.csv`);
  };
  const exportPdf = async () => {
    try {
      const [{ jsPDF }, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
      const autoTable = autoTableMod.default || autoTableMod.autoTable;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setFontSize(16); doc.text(`${config.name} — KPI report (${days} days)`, 40, 50);
      doc.setFontSize(9); doc.setTextColor(110); doc.text(`FlowSense MOW+ · generated ${new Date().toLocaleString()} · source: ${history?.sources?.[kpiKey] || 'n/a'}`, 40, 66);
      doc.setTextColor(30); doc.setFontSize(10);
      const lines = doc.splitTextToSize(summary || 'No data in this period.', 515);
      doc.text(lines, 40, 90);
      let y = 90 + lines.length * 13 + 10;
      doc.setFontSize(11); doc.text('Recommended actions', 40, y); y += 14; doc.setFontSize(9);
      recs.forEach((r) => { const l = doc.splitTextToSize(`• [${r.when}] ${r.title}: ${r.text}`, 515); doc.text(l, 40, y); y += l.length * 12; });
      autoTable(doc, {
        startY: y + 10, head: [['Date', 'Value', 'Target', 'Unusual']],
        body: rows.map(r => [r.date, fmtVal(r.value, config.unit), fmtVal(r.target, config.unit), r.anomaly ? 'yes' : '']),
        styles: { fontSize: 8 }, headStyles: { fillColor: [128, 90, 213] },
      });
      doc.save(`${fileBase}.pdf`);
    } catch (e) {
      toast({ title: 'PDF export failed', description: e.message, status: 'error', duration: 5000 });
    }
  };
  const printReport = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast({ title: 'Allow pop-ups to print the report', status: 'warning', duration: 4000 }); return; }
    w.document.write(`<!doctype html><html><head><title>${escapeHtml(config.name)} report</title><style>body{font-family:system-ui,sans-serif;padding:24px;color:#1a202c}table{border-collapse:collapse;width:100%;font-size:12px}td,th{border:1px solid #e2e8f0;padding:4px 8px;text-align:left}th{background:#f7fafc}.a{color:#c53030;font-weight:600}</style></head><body>
      <h2>${escapeHtml(config.name)} — KPI report (${days} days)</h2><p style="color:#718096;font-size:12px">FlowSense MOW+ · ${escapeHtml(new Date().toLocaleString())}</p>
      <p>${escapeHtml(summary || 'No data in this period.')}</p><h3>Recommended actions</h3><ul>${recs.map(r => `<li><b>${escapeHtml(r.when)} — ${escapeHtml(r.title)}:</b> ${escapeHtml(r.text)}</li>`).join('')}</ul>
      <table><thead><tr><th>Date</th><th>Value</th><th>Target</th><th>Unusual</th></tr></thead><tbody>${rows.map(r => `<tr><td>${r.date}</td><td class="${r.anomaly ? 'a' : ''}">${escapeHtml(fmtVal(r.value, config.unit))}</td><td>${escapeHtml(fmtVal(r.target, config.unit))}</td><td>${r.anomaly ? 'yes' : ''}</td></tr>`).join('')}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };
  const emailReport = () => {
    const subject = `FlowSense KPI report — ${config.name} (${days} days)`;
    const body = `${summary || 'No data in this period.'}\n\nRecommended actions:\n${recs.map(r => `- [${r.when}] ${r.title}: ${r.text}`).join('\n')}\n\n(Export the CSV or PDF from the report to attach the daily figures.)`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!kpiKey) return null;
  const noData = !loading && history && (unavailable || !s.vals.length);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent bg={bg} borderRadius="xl" overflow="hidden">
        <Box h="4px" bgGradient={headerGradient} />
        <ModalHeader bg={cardBg} borderBottomWidth="1px" borderColor={borderColor} pb={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} pr={8}>
            <HStack spacing={3}>
              <Box p={2} bg={tint} borderRadius="lg"><Icon as={MdAnalytics} color={accent} boxSize={6} /></Box>
              <VStack align="start" spacing={0}>
                <Heading size="md">{config.name} — Full Report</Heading>
                <Text fontSize="sm" color={muted}>{history?.sources?.[kpiKey] ? `Source: ${history.sources[kpiKey]}` : 'FlowSense Analytics'}</Text>
              </VStack>
            </HStack>
            <HStack spacing={2}>
              <Select size="sm" value={timeframe} onChange={(e) => setTimeframe(e.target.value)} w="140px" borderRadius="md" aria-label="Period">
                <option value="7D">Last 7 Days</option>
                <option value="30D">Last 30 Days</option>
                <option value="90D">Last 90 Days</option>
              </Select>
              <Button size="sm" leftIcon={<MdPrint />} variant="outline" onClick={printReport} isDisabled={!s.vals.length}>Print</Button>
              <Menu>
                <MenuButton as={Button} size="sm" colorScheme={config.color} leftIcon={<MdFileDownload />} rightIcon={<MdExpandMore />} isDisabled={!s.vals.length}>Export</MenuButton>
                <MenuList>
                  <MenuItem onClick={exportPdf}>PDF report</MenuItem>
                  <MenuItem onClick={exportCsv}>CSV (daily values)</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />

        <ModalBody p={6}>
          {error && (
            <Alert status="error" borderRadius="lg" mb={4}><AlertIcon /><AlertDescription flex="1">Could not load the history: {error.message}</AlertDescription><Button size="sm" onClick={load}>Retry</Button></Alert>
          )}
          {noData && (
            <Alert status="info" borderRadius="lg" mb={4}>
              <AlertIcon />
              <AlertDescription>{unavailable ? `${config.name} has no daily history in the FlowSense database, so only the current value is shown. Nothing is estimated.` : `No ${config.name.toLowerCase()} data in the last ${days} days.`}</AlertDescription>
            </Alert>
          )}
          <Tabs variant="soft-rounded" colorScheme={config.color} isLazy>
            <TabList mb={6} flexWrap="wrap" gap={1}>
              <Tab><Icon as={MdTimeline} mr={2} /> Performance Trends</Tab>
              <Tab><Icon as={MdPsychology} mr={2} /> Analysis & Actions</Tab>
              <Tab><Icon as={MdCompareArrows} mr={2} /> Metric Correlations</Tab>
              <Tab><Icon as={MdWarning} mr={2} /> Anomaly Log{s.anomalies.length ? <Badge ml={2} colorScheme="red">{s.anomalies.length}</Badge> : null}</Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                <VStack spacing={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                    <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                      <Text fontSize="sm" color={muted}>Current</Text>
                      <Heading size="lg" mt={1}>{fmtVal(s.current, config.unit)}</Heading>
                      <Text fontSize="xs" color={muted}>period average {fmtVal(s.avg, config.unit)}</Text>
                    </Box>
                    <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                      <Text fontSize="sm" color={muted}>Trend over period</Text>
                      {loading ? <Skeleton h="32px" mt={1} /> : (
                        <HStack mt={1}>
                          <Icon as={TrendIcon} color={good === null ? 'gray.400' : good ? 'green.500' : 'red.500'} boxSize={6} />
                          <Heading size="lg" color={good === null ? undefined : good ? 'green.500' : 'red.500'}>{trendPct === null ? '—' : `${Math.abs(trendPct).toFixed(1)}%`}</Heading>
                        </HStack>
                      )}
                      <Text fontSize="xs" color={muted}>{s.trend ? `least-squares fit, ${s.trend.n} days` : 'not enough data'}</Text>
                    </Box>
                    <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                      <Text fontSize="sm" color={muted}>Target</Text>
                      <Heading size="lg" mt={1}>{fmtVal(dailyTarget, config.unit)}</Heading>
                      <Text fontSize="xs" color={muted}>{config.perDayTarget ? `per day (${fmtVal(config.target, config.unit)} / 30 days)` : config.higherIsBetter ? 'or higher' : 'or lower'}</Text>
                    </Box>
                    <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                      <Text fontSize="sm" color={muted}>Days on target</Text>
                      <Heading size="lg" mt={1}>{adherence === null ? '—' : `${adherence}%`}</Heading>
                      <Progress value={adherence || 0} colorScheme={config.color} size="sm" borderRadius="full" mt={2} />
                    </Box>
                  </SimpleGrid>

                  <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                    <Heading size="sm" mb={4}>Daily history</Heading>
                    {loading && !history ? <Skeleton h="400px" /> : (
                      <Box h="400px">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={s.chart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`kpiFill-${kpiKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={accent} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={accent} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="label" fontSize={12} tickMargin={10} />
                            <YAxis fontSize={12} />
                            <RechartsTooltip contentStyle={{ borderRadius: '10px' }} formatter={(value, name) => [fmtVal(value, config.unit), name]} />
                            <Legend verticalAlign="top" height={36} />
                            <Area type="monotone" dataKey="value" stroke="none" fillOpacity={1} fill={`url(#kpiFill-${kpiKey})`} name={config.name} legendType="none" connectNulls isAnimationActive={false} />
                            <Line type="monotone" dataKey="value" stroke={accent} strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 6 }} name={config.name} connectNulls isAnimationActive={false} />
                            <Line type="monotone" dataKey="target" stroke="var(--chakra-colors-gray-400)" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" isAnimationActive={false} />
                            <Scatter dataKey="anomaly" fill="var(--chakra-colors-red-500)" name="Unusual day" isAnimationActive={false} />
                            {s.chart.length > 14 && <Brush dataKey="label" height={26} stroke={accent} fill={brushFill} />}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </Box>
                </VStack>
              </TabPanel>

              <TabPanel p={0}>
                <VStack spacing={6} align="stretch">
                  <Box p={6} bg={tint} borderRadius="xl" borderLeftWidth="4px" borderColor={`${config.color}.500`}>
                    <HStack mb={3}>
                      <Icon as={MdLightbulb} color={accent} boxSize={6} />
                      <Heading size="md">Analysis</Heading>
                      <Badge variant="subtle">computed from {s.vals.length} day(s) of FlowSense data</Badge>
                    </HStack>
                    {loading && !history ? <Skeleton h="80px" /> : <Text fontSize="md" lineHeight="tall" color={bodyText}>{summary || 'There is no data for this KPI in the selected period.'}</Text>}
                  </Box>
                  <Heading size="sm">Recommended actions</Heading>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    {recs.map(r => (
                      <Box key={r.title} p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                        <Badge colorScheme={r.color} mb={2}>{r.when}</Badge>
                        <Text fontSize="sm" fontWeight="bold" mb={2}>{r.title}</Text>
                        <Text fontSize="xs" color={muted} mb={3}>{r.text}</Text>
                        <Link as={RouterLink} to={r.link} fontSize="xs" color={accent} onClick={onClose}>{r.linkLabel} →</Link>
                      </Box>
                    ))}
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              <TabPanel p={0}>
                <Box p={6} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflowX="auto">
                  <Heading size="sm" mb={1}>How {config.name} moves with the other KPIs</Heading>
                  <Text fontSize="xs" color={muted} mb={4}>Pearson correlation of the daily values over the selected period. Correlation is not causation.</Text>
                  <Table variant="simple" size="md">
                    <Thead bg={theadBg}><Tr><Th>KPI</Th><Th>Relationship</Th><Th isNumeric>r</Th><Th>Strength</Th><Th isNumeric>Days</Th></Tr></Thead>
                    <Tbody>
                      {s.correlations.map(c => {
                        const [label, color] = strength(c.r);
                        return (
                          <Tr key={c.key}>
                            <Td fontWeight="bold">{c.name}</Td>
                            <Td fontSize="sm">{Math.abs(c.r) < 0.2 ? 'No clear relationship' : `${c.r > 0 ? 'Rises' : 'Falls'} when ${config.name} rises${c.lagged ? ' — one day later' : ''}`}</Td>
                            <Td isNumeric>{c.r.toFixed(2)}</Td>
                            <Td><Badge colorScheme={color}>{label}</Badge></Td>
                            <Td isNumeric>{c.n}</Td>
                          </Tr>
                        );
                      })}
                      {!s.correlations.length && <Tr><Td colSpan={5}><Text fontSize="sm" color={muted}>{loading ? 'Loading…' : 'Not enough overlapping days with variation to compute correlations.'}</Text></Td></Tr>}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>

              <TabPanel p={0}>
                <Box p={6} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm">Unusual days ({timeframe})</Heading>
                    <Badge colorScheme={s.anomalies.length ? 'red' : 'green'}>{s.anomalies.length} flagged</Badge>
                  </Flex>
                  {s.anomalies.length === 0 ? (
                    <Text color={muted}>No day deviated more than 2.5 standard deviations from the previous week.</Text>
                  ) : (
                    <Table variant="simple" size="sm">
                      <Thead><Tr><Th>Date</Th><Th isNumeric>Value</Th><Th isNumeric>Expected (7-day avg)</Th><Th isNumeric>Deviation</Th></Tr></Thead>
                      <Tbody>
                        {[...s.anomalies].reverse().map(a => (
                          <Tr key={a.date}>
                            <Td fontWeight="medium">{shortDate(a.date)}</Td>
                            <Td isNumeric fontWeight="bold" color="red.500">{fmtVal(a.value, config.unit)}</Td>
                            <Td isNumeric>{fmtVal(a.expected, config.unit)}</Td>
                            <Td isNumeric>{a.z === null ? 'flat baseline' : `${a.z > 0 ? '+' : ''}${a.z.toFixed(1)} σ`}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter bg={bg} borderTopWidth="1px" borderColor={borderColor}>
          <Button variant="ghost" mr={3} onClick={onClose}>Close Report</Button>
          <Button colorScheme={config.color} leftIcon={<MdEmail />} onClick={emailReport} isDisabled={!summary}>Email summary</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default KPIFullReportModal;
